'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useGetUser, useWallets } from '@morfi/backend-sdk-v2/client';
import { Suspense, useEffect, useRef } from 'react';
import PortfolioLoading from '@/app/[locale]/portfolio/loading';
import { FundingReceiptLink } from '@/components/onramp/funding-receipt-link';
import { PortfolioBody } from '@/components/portfolio/portfolio-body';
import { PortfolioContext } from '@/components/portfolio/portfolio-context';
import { UnauthenticatedPrompt } from '@/components/portfolio/unauthenticated-prompt';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
interface OwnPortfolioProps {
  readonly perfRoute?: 'portfolio' | 'profile';
  readonly perfInstanceKey?: string;
}
interface ConnectedProvider {
  provider: string;
  username?: string;
  displayName?: string;
}
interface PublicProfileError extends Error {
  readonly status?: number;
  readonly apiCode?: string;
}

/**
 * Own-profile renderer. Uses the SAME `useGetUser` SDK call as
 * `<OtherPortfolio>` so the public-facing profile chrome (display name,
 * avatar, hero, NSFW flags, bio, badges) renders identically whether the
 * viewer is logged in as themselves or browsing in incognito. Auth context
 * supplies only the private fields the public endpoint doesn't expose
 * (email, wallets, account-creation timestamp, primary wallet address).
 */
export function OwnPortfolio(t0) {
  const $ = _c(57);
  const {
    perfRoute: t1,
    perfInstanceKey
  } = t0;
  const perfRoute = t1 === undefined ? "portfolio" : t1;
  const {
    user,
    isLoading: authLoading,
    isAuthenticated
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletEnabled,
    primaryWallet: gatedPrimaryWallet
  } = useMultiWalletFeature();
  const userId = user?.id ?? "";
  const usableGatedPrimary = gatedPrimaryWallet !== null && gatedPrimaryWallet.archivedAt === null && gatedPrimaryWallet.isAvailable !== false ? gatedPrimaryWallet : null;
  let t2;
  if ($[0] !== multiWalletEnabled || $[1] !== usableGatedPrimary || $[2] !== wallets) {
    t2 = multiWalletEnabled ? wallets : usableGatedPrimary === null ? [] : [usableGatedPrimary];
    $[0] = multiWalletEnabled;
    $[1] = usableGatedPrimary;
    $[2] = wallets;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const portfolioWallets = t2;
  let t3;
  if ($[4] !== multiWalletEnabled || $[5] !== usableGatedPrimary || $[6] !== wallets) {
    t3 = multiWalletEnabled ? wallets.find(_temp) ?? wallets[0] ?? null : usableGatedPrimary;
    $[4] = multiWalletEnabled;
    $[5] = usableGatedPrimary;
    $[6] = wallets;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const primaryWalletForRealtime = t3;
  let t4;
  if ($[8] !== portfolioWallets) {
    t4 = portfolioWallets.map(_temp2);
    $[8] = portfolioWallets;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const realtimeWalletPubkeys = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [...PORTFOLIO_USER_PROFILE_FIELDS];
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== realtimeWalletPubkeys || $[12] !== userId) {
    t6 = {
      identifier: userId,
      walletPubkeys: realtimeWalletPubkeys,
      fields: t5
    };
    $[11] = realtimeWalletPubkeys;
    $[12] = userId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = userId.length > 0;
  let t8;
  if ($[14] !== t7) {
    t8 = {
      enabled: t7
    };
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: publicUserForProfile,
    error: publicProfileError,
    isError: publicProfileFailed
  } = useGetUser(t6, t8);
  const t9 = !authLoading && (!isAuthenticated || publicUserForProfile !== undefined);
  let t10;
  if ($[16] !== perfInstanceKey) {
    t10 = perfInstanceKey !== undefined ? {
      instanceKey: perfInstanceKey
    } : {};
    $[16] = perfInstanceKey;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== perfRoute || $[19] !== t10 || $[20] !== t9) {
    t11 = {
      name: perfRoute,
      isReady: t9,
      ...t10
    };
    $[18] = perfRoute;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  useRoutePerf(t11);
  const reportedPublicProfileError = useRef(null);
  let t12;
  let t13;
  if ($[22] !== publicProfileError || $[23] !== publicProfileFailed) {
    t12 = () => {
      if (!publicProfileFailed) {
        reportedPublicProfileError.current = null;
        return;
      }
      if (publicProfileError === reportedPublicProfileError.current) {
        return;
      }
      reportedPublicProfileError.current = publicProfileError;
      logger.warn("portfolio.public_profile.load_failed", {
        stage: "public_profile",
        ...publicProfileErrorMetadata(publicProfileError)
      });
    };
    t13 = [publicProfileError, publicProfileFailed];
    $[22] = publicProfileError;
    $[23] = publicProfileFailed;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  useEffect(t12, t13);
  if (authLoading) {
    let t14;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <PortfolioLoading />;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    return t14;
  }
  if (!isAuthenticated || user === null) {
    let t14;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <UnauthenticatedPrompt />;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    return t14;
  }
  const primaryWallet = primaryWalletForRealtime;
  const primaryWalletPubkey = primaryWallet?.address ?? null;
  const financialQueryId = multiWalletEnabled ? userId : primaryWalletPubkey ?? "";
  const walletPubkey = primaryWalletPubkey ?? "";
  const publicRecord = publicUserForProfile as Record<string, unknown> | undefined;
  let t14;
  if ($[28] !== publicRecord) {
    t14 = Array.isArray(publicRecord?.connectedProviders) ? publicRecord.connectedProviders as ConnectedProvider[] : [];
    $[28] = publicRecord;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const connectedProviders = t14;
  let t15;
  if ($[30] !== financialQueryId || $[31] !== multiWalletEnabled || $[32] !== primaryWalletPubkey || $[33] !== realtimeWalletPubkeys || $[34] !== userId || $[35] !== walletPubkey) {
    t15 = {
      viewerMode: "own",
      userId,
      financialQueryId,
      primaryWalletPubkey,
      walletPubkey,
      walletPubkeys: realtimeWalletPubkeys,
      multiWalletEnabled
    };
    $[30] = financialQueryId;
    $[31] = multiWalletEnabled;
    $[32] = primaryWalletPubkey;
    $[33] = realtimeWalletPubkeys;
    $[34] = userId;
    $[35] = walletPubkey;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== user.id) {
    t16 = <Suspense fallback={null}><FundingReceiptLink userId={user.id} /></Suspense>;
    $[37] = user.id;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = portfolioWallets as import('@/types/auth').Wallet[];
  const t18 = primaryWallet as import('@/types/auth').Wallet | null;
  const t19 = publicRecord?.displayName as string | null ?? user.displayName ?? null;
  const t20 = publicRecord?.handle as string | null ?? user.handle ?? null;
  const t21 = publicUserForProfile === undefined ? user.bio ?? null : publicUserForProfile.bio ?? null;
  const t22 = publicRecord?.avatarUrl as string | null ?? user.avatarUrl ?? null;
  const t23 = publicRecord?.avatarNsfw as boolean | null | undefined ?? null;
  const t24 = (publicRecord?.avatarPreferences as {
    rasterise?: boolean;
  } | null | undefined)?.rasterise ?? user.avatarPreferences?.rasterise ?? null;
  const t25 = publicRecord?.heroUrl as string | null | undefined ?? user.heroUrl ?? null;
  const t26 = publicRecord?.heroNsfw as boolean | null | undefined ?? null;
  let t27;
  if ($[39] !== connectedProviders || $[40] !== t17 || $[41] !== t18 || $[42] !== t19 || $[43] !== t20 || $[44] !== t21 || $[45] !== t22 || $[46] !== t23 || $[47] !== t24 || $[48] !== t25 || $[49] !== t26 || $[50] !== user.createdAt || $[51] !== user.id) {
    t27 = <PortfolioBody creatorUserId={user.id} registeredAt={user.createdAt} wallets={t17} primaryWallet={t18} displayName={t19} handle={t20} bio={t21} avatarUrl={t22} avatarNsfw={t23} avatarRasterise={t24} heroUrl={t25} coverNsfw={t26} connectedProviders={connectedProviders} />;
    $[39] = connectedProviders;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
    $[50] = user.createdAt;
    $[51] = user.id;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t15 || $[54] !== t16 || $[55] !== t27) {
    t28 = <PortfolioContext.Provider value={t15}>{t16}{t27}</PortfolioContext.Provider>;
    $[53] = t15;
    $[54] = t16;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  return t28;
}
function _temp2(wallet_0) {
  return wallet_0.address;
}
function _temp(wallet) {
  return wallet.isPrimary;
}
function publicProfileErrorMetadata(error: Error | null): {
  readonly errorName: string;
  readonly status: number | null;
  readonly apiCode: string | null;
} {
  const errorWithMetadata = error as PublicProfileError | null;
  return {
    errorName: errorWithMetadata?.name ?? 'Error',
    status: errorWithMetadata?.status ?? null,
    apiCode: errorWithMetadata?.apiCode ?? null
  };
}